import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Loader2, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iter 148 · ICD-10 auto-extract widget.
 *
 * Paste-a-summary panel. Planners paste the discharge narrative
 * they already have in their hand and the widget calls
 * ``POST /api/matching/icd10/extract`` (BAA-locked Vertex path,
 * PHI scrubbed server-side). The returned codes are surfaced as
 * clickable chips so the planner can accept the ones that look
 * right — the matching engine's precise ICD-10 scoring path (A-6)
 * uses them without manual tagging.
 *
 * Props:
 *   onApply(codes: string[], primaryDescription: string): void
 *     Fired when the planner clicks "Apply". The parent decides how
 *     to hydrate its form (usually: replace the primary diagnosis
 *     text and stash the codes for submission).
 */
export function Icd10ExtractPanel({ onApply, disabled }) {
  const { getAuthHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [picked, setPicked] = useState(new Set());

  const run = useCallback(async () => {
    if (text.trim().length < 12) {
      toast.error('Paste at least a sentence of clinical narrative.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/matching/icd10/extract`,
        { text: text.trim(), max_codes: 6 },
        getAuthHeader(),
      );
      setCodes(data.codes || []);
      setWarnings(data.warnings || []);
      // Auto-pick the top-2 codes if the model returned any.
      const top = new Set((data.codes || []).slice(0, 2).map(c => c.code));
      setPicked(top);
      if (!data.codes?.length) {
        toast.info('No codes extracted — try a longer or clearer summary.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'ICD-10 extraction failed');
    } finally {
      setBusy(false);
    }
  }, [text, getAuthHeader]);

  const toggle = (code) => {
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const apply = () => {
    const list = codes.filter(c => picked.has(c.code));
    if (!list.length) {
      toast.error('Pick at least one code first.');
      return;
    }
    const primaryDesc = list[0].description || list[0].code;
    onApply?.(list.map(c => c.code), primaryDesc);
    toast.success(`Applied ${list.length} ICD-10 code(s)`);
    // collapse after apply
    setOpen(false);
  };

  return (
    <div
      className="rounded-xl border border-dashed border-indigo-500/40 bg-indigo-500/5"
      data-testid="icd10-extract-panel"
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={disabled}
        className="w-full flex items-center justify-between px-4 py-3 text-sm text-indigo-300 hover:text-indigo-200 disabled:opacity-50"
        data-testid="icd10-toggle"
      >
        <span className="flex items-center gap-2">
          <Sparkles className="h-4 w-4" />
          <span className="font-medium">AI · Auto-extract ICD-10 from a discharge summary</span>
          <span className="text-xs text-slate-500">(BAA-locked, PHI scrubbed)</span>
        </span>
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {open && (
        <div className="p-4 border-t border-indigo-500/20 space-y-3">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste the discharge summary or intake note. Names / DOBs will be scrubbed before the LLM sees them."
            rows={5}
            className="w-full text-sm rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 focus:outline-none focus:border-indigo-500/60"
            data-testid="icd10-text"
            disabled={busy}
          />

          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">
              {text.length} / 20,000 chars
            </span>
            <Button
              type="button"
              onClick={run}
              disabled={busy || text.trim().length < 12}
              size="sm"
              data-testid="icd10-run"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Sparkles className="h-4 w-4 mr-1.5" /> Extract</>)}
            </Button>
          </div>

          {warnings.length > 0 && (
            <div className="text-xs text-amber-300 space-y-1" data-testid="icd10-warnings">
              {warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
            </div>
          )}

          {codes.length > 0 && (
            <div className="space-y-2" data-testid="icd10-results">
              <div className="text-xs text-slate-400">Tap to include:</div>
              <div className="flex flex-wrap gap-2">
                {codes.map((c) => {
                  const on = picked.has(c.code);
                  return (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => toggle(c.code)}
                      className={`text-xs rounded-full border px-3 py-1.5 transition-colors ${
                        on
                          ? 'bg-indigo-500/25 border-indigo-500/60 text-indigo-200'
                          : 'bg-slate-900 border-slate-800 text-slate-300 hover:border-slate-700'
                      }`}
                      data-testid={`icd10-chip-${c.code}`}
                    >
                      <span className="font-mono font-semibold">{c.code}</span>
                      {c.description && (
                        <span className="ml-2 text-slate-400">{c.description}</span>
                      )}
                      <span className="ml-2 text-slate-500">{Math.round(c.confidence * 100)}%</span>
                    </button>
                  );
                })}
              </div>

              <div className="flex justify-end pt-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={apply}
                  disabled={picked.size === 0}
                  data-testid="icd10-apply"
                >
                  Apply {picked.size} code{picked.size === 1 ? '' : 's'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
